import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { BoxShadowLarge } from 'autogen/design-tokens/tokens';
import { CustomerServiceButton } from 'autogen/translation-keys/trans-website-chat';
import {
    AcceptMarketingCookiesToSeeElementBody,
    ChatNotAllowed,
    Close
} from 'autogen/translation-keys/trans-website-shared';
import { Button } from 'components/Button';
import CookieInfobox from 'components/CookieInfobox';
import Icon from 'components/Icon';
import useFeatureToggles from 'hooks/user/useFeatureToggles';
import useHasInformationCookieBeenAccepted from 'hooks/user/useHasInformationCookieBeenAccepted';
import { CookieCategory } from 'modules/helpers/cookieInformationHelper';
import { removeButtonStyling } from 'modules/helpers/mixins';
import WebsiteSettings from 'modules/websiteSettings';
import { breakpoint } from 'variables';
import Zowie from './Zowie.svg?react';
import initZowie from './zowie';

const ZowieCookieButtonWrapper = styled.div`
    position: fixed;
    display: flex;
    flex-direction: column;
    align-items: end;
    bottom: 25px;
    right: 25px;
    z-index: 30001;
    gap: ${({ theme }) => theme.Spacing8};
`;

const ChatButtonReplica = styled.button`
    ${removeButtonStyling}
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    box-shadow: rgba(0, 0, 0, 0.3) 0px 0px 40px;
    border-radius: 8px;
    cursor: pointer;
    width: 45px;
    height: 45px;
    opacity: 1;
    transition: opacity 0.3s linear 0.1s;
    @starting-style {
        opacity: 0;
    }

    ${breakpoint.up('768px')} {
        width: 60px;
        height: 60px;
    }
`;

const CookieBoxWrapper = styled.div`
    box-shadow: ${BoxShadowLarge};
    border-radius: ${({ theme }) => theme.RadiusMedium};
    position: relative;
`;

const CookieBoxCloseButton = styled(Button)`
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
`;

const ZowieRenderer = () => {
    const { isZowieEnabled } = WebsiteSettings.liveChat;

    const { t } = useTranslation();
    const isMarketingCookieAccepted = useHasInformationCookieBeenAccepted(CookieCategory.marketing);
    const { disable3rdParty } = useFeatureToggles();

    const [showMissingConsentOverlay, setShowMissingConsentOverlay] = useState(false);
    const [hasInitializedZowie, setHasInitializedZowie] = useState(false);

    useEffect(() => {
        if (hasInitializedZowie || !isZowieEnabled || disable3rdParty) return;

        if (isMarketingCookieAccepted) {
            initZowie();
            setHasInitializedZowie(true);
        }
    }, [isMarketingCookieAccepted, hasInitializedZowie, isZowieEnabled, disable3rdParty]);

    const handleChatReplicaBtnClick = () => {
        setShowMissingConsentOverlay(true);
    };

    if (isMarketingCookieAccepted || disable3rdParty || !isZowieEnabled) return null;

    return (
        <ZowieCookieButtonWrapper>
            {showMissingConsentOverlay && (
                <CookieBoxWrapper>
                    <CookieBoxCloseButton
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowMissingConsentOverlay(false)}
                        aria-label={t(Close)}
                    >
                        <Icon type="Close" size={16} />
                    </CookieBoxCloseButton>
                    <CookieInfobox
                        callback={() => setShowMissingConsentOverlay(false)}
                        headline={t(ChatNotAllowed)}
                        body={t(AcceptMarketingCookiesToSeeElementBody)}
                    />
                </CookieBoxWrapper>
            )}
            <ChatButtonReplica onClick={handleChatReplicaBtnClick} aria-label={t(CustomerServiceButton)}>
                <Zowie />
            </ChatButtonReplica>
        </ZowieCookieButtonWrapper>
    );
};

export default ZowieRenderer;
